import type { ComponentPropsWithoutRef, ReactNode } from "react";
import { cn } from "@/shared/utils/cn";

type Variant = "primary" | "secondary";

type ItemProps = ComponentPropsWithoutRef<"div"> & {
  borderRight?: string;
  p?: string | number;
  bg?: string;
};

type ListTabProps = ComponentPropsWithoutRef<"div"> & {
  variant?: Variant;
};

const ListTab = ({
  children,
  className,
  variant = "primary",
  ...props
}: ListTabProps) => {
  return (
    <div
      className={cn(
        "mb-5 inline-flex max-w-full gap-1 overflow-x-auto rounded-lg border border-border bg-grey-secondary p-1 shadow-sm",
        variant === "secondary" && "flex w-full min-w-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

type ListTabItemProps = Omit<ItemProps, "children"> & {
  active?: boolean;
  last?: boolean;
  children?: ReactNode;
  variant?: Variant;
};

export const ListTabItem = ({
  active,
  children,
  className,
  variant = "primary",
  ...props
}: ListTabItemProps) => {
  return (
    <div
      className={cn(
        "whitespace-nowrap cursor-pointer rounded-md font-medium text-text-secondary transition-[color,background-color,box-shadow] duration-200",
        "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",

        variant === "primary" && "shrink-0 px-5 py-3 text-btn-tertiary",
        variant === "secondary" && "flex-1 px-3 py-2 text-center mr-4",

        active
          ? "bg-white font-semibold text-primary shadow-sm"
          : "hover:bg-white/80 hover:text-text",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
};

export default ListTab;
